/* ────────────────────────────────────────────────────────────────
   RS Rest Easy — claim handler for /pages/rest-easy

   ⚠️ THE FLOW IS INVERTED FROM WHAT THIS FILE ORIGINALLY DID, and the
   header used to describe the old one. It used to post the claim on
   one tap while the visitor was signed out, then invite them to sign
   in afterwards. Money was announced before the person had done
   anything. The account now comes first:

     SIGNED OUT (pre-auth)
       read ?t= and ?e=, scrub both from the address bar, and bind the
       CTA. The CTA POSTS NOTHING. On click it writes the token, the
       address and the consent tick to sessionStorage and navigates to
       login with a return_to back to this page.

     SIGNED IN (post-auth)
       look for a pending claim in sessionStorage.
         none      — the ordinary visit. Leave the `in` panel up and,
                     critically, DO NOT SCRUB THE URL.
         mismatch  — the address signed in is not the address the gift
                     was sent to. Show `mismatch`. Do not post, and do
                     not clear storage; signing out and back in resumes.
         match     — clear storage, then post, then confirm or error.

   ⚠️ THE SIGNED-IN EARLY RETURN IS GONE. This file used to bail on
   data-rs-signed-in before touching anything, and that bail was doing
   real work: it kept the URL scrub away from a signed-in visitor
   holding an unclaimed ?t=, whose address bar is their only copy of
   an unused gift. The hazard is unchanged; the guard moved. It is now
   the no-pending-claim path in initSignedIn(), which deliberately does
   NOT scrub. That is easy to lose in a refactor because it now reads
   as an omission rather than as a return — see the note there.

   THIS FILE HOLDS NO USER-FACING COPY, WITH ONE NAMED EXCEPTION. Every
   state on both arms is server-rendered by
   sections/rs-rest-easy-hero.liquid from schema settings, and this
   script only toggles which one is shown. If a message needs changing,
   it changes in the Theme Editor, not here.

   The exception is the recipient's address, written into an account
   box on the mismatch panel (and still into the retired done panel's
   box on the signed-out arm). It is not copy — it is the visitor's own
   data arriving in the URL — and it is written with textContent, never
   innerHTML. There is no innerHTML anywhere in this file.

   EACH ARM RENDERS ITS DEFAULT PANEL VISIBLE and this script swaps
   away from it. Signed out that is `claim`, swapping to `error` when
   the token is missing. Signed in it is `in`, swapping to `claiming`
   when a pending claim is found. The direction is deliberate on both:
   only JS can read the query string and sessionStorage, so the server
   cannot know which state is correct, and whichever panel ships
   visible is the one that never flashes.

   ⚠️ THIS IS THE FIRST CROSS-ORIGIN REQUEST IN THIS THEME. Every
   other fetch/XHR in the codebase targets a same-origin Shopify
   endpoint. See the CORS notes at postClaim() before changing the
   request shape — the failure modes here are not the familiar ones.

   Self-loaded (defer) from the section file; layout/share.liquid
   loads no section JS of its own.
   ──────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.rsRestEasyLoaded) return; // window-level double-load guard
  window.rsRestEasyLoaded = true;

  var ROOT_SELECTOR = '[data-rs-rest-easy]';
  var SIGNED_IN_ATTR = 'data-rs-signed-in';
  var TOKEN_PARAM = 't';
  var EMAIL_PARAM = 'e';
  var EMAIL_NODE_SELECTOR = '[data-rs-rest-email]';
  var MISMATCH_EMAIL_SELECTOR = '[data-rs-mismatch-email]';
  var EMAIL_HIDDEN_CLASS = 'rs-rest__account-email--hidden';
  var EMAIL_MAX_LENGTH = 254; // RFC 5321 maximum; longer is not an address
  var REQUEST_TIMEOUT_MS = 10000;

  /* ── sessionStorage keys ─────────────────────────────────────────

     THREE KEYS, NOT ONE OBJECT — a decision taken against the advice
     given when this was scoped, so the trade is recorded here rather
     than lost. One JSON object would be atomic: three keys can desync,
     because there is no write that lands all three or none, and a
     token recovered without its address has no defined meaning.

     Mitigated rather than ignored. readPending() below treats the set
     as valid ONLY when the token is present, and it defaults consent
     to false whenever its key is missing or unreadable — so a torn
     write under-collects consent rather than over-collecting it, which
     is the only direction a consent bug is allowed to fail in. Anyone
     revisiting this should read that function before assuming three
     independent reads are safe.

     NAMING sits between the theme's two live conventions:
     rs_snake_case is used for flags and scalars (rs_teaser_popup_seen)
     and rs:kebab-namespace for structured
     hand-offs across a navigation (rs:review-handoff, rs:swatch-return,
     rs:teaser-popup-return). These are hand-off values in scalar form,
     and the names below were specified directly. They collide with
     nothing: all nine existing keys were enumerated before these were
     added. */
  var KEY_TOKEN = 'rs-claim-token';
  var KEY_EMAIL = 'rs-claim-email';
  var KEY_CONSENT = 'rs-claim-consent';

  /* A FOURTH KEY, added with the storage-restore path. The three above
     were specified; this one is forced by the expiry check — there is
     nothing else to measure age against, because the claim token is
     opaque and carries no timestamp of its own. */
  var KEY_TS = 'rs-claim-ts';

  /* ⚠️ SIX HOURS, AND BE PRECISE ABOUT WHAT THIS IS.

     It is a STORAGE FRESHNESS BOUND that this file chose. It is NOT a
     signature window: the claim token is an opaque ?t= value with no
     theme-side expiry, and nothing here can verify it. Do not confuse
     it with the share surface's HMAC `exp`, which is a real signed
     window over "<customer.id>|<exp>" — that belongs to Scenario 1 and
     does not exist on this flow. The two have been conflated more than
     once; they are unrelated mechanisms that happen to share a number.

     WHAT IT BUYS: a token restored from a tab left open for hours is
     more likely stale than good, and a stale claim is dropped silently
     at the webhook — no verdict comes back, so the page would show the
     confirmation for a claim that went nowhere. Failing to the error
     panel instead is the honest outcome.

     ⚠️ IF SCENARIO 2 ENFORCES ITS OWN TTL, THIS MUST MATCH IT. Nothing
     synchronises them and a mismatch is silent in both directions: too
     long and we show a claim UI for a token the webhook will reject,
     too short and we refuse a token that would still have worked. */
  var CLAIM_TTL_MS = 21600000; // 6 hours

  /* Every sessionStorage access is wrapped. It THROWS OUTRIGHT in some
     privacy modes — not returns null, throws — so an unguarded getItem
     here would take down the whole claim before it started. (The
     teaser popup, removed 2026-10-30, wrapped all three operations for
     exactly this reason.) */
  function readKey(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeKey(key, value) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function clearKey(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      /* non-fatal */
    }
  }

  /* Normalisation for the mismatch comparison. Must stay identical to
     the Liquid that produces data-rs-customer-email — `| downcase |
     strip` — or the guard compares two differently-shaped strings and
     rejects a legitimate claimant on casing alone. */
  function normalizeEmail(value) {
    return (value || '').trim().toLowerCase();
  }

  function clearPending() {
    clearKey(KEY_TOKEN);
    clearKey(KEY_EMAIL);
    clearKey(KEY_CONSENT);
    clearKey(KEY_TS);
  }

  /* THE ONE READER OF THE STORED CLAIM. Returns {token, email, consent}
     or null, and BOTH ARMS GO THROUGH IT — the signed-out arm to
     restore a claim after a sign-out round trip, the signed-in arm to
     find the claim it is about to fire. Sharing it is deliberate: two
     readers would be two places for the expiry rule and the consent
     coercion to drift.

     EXPIRED OR UNDATED TOKENS ARE CLEARED, NOT JUST REFUSED. Leaving a
     dead token in storage means every subsequent load on this tab
     re-reads it, re-fails, and shows the error panel again — a page
     stuck in a state the visitor cannot clear. Clearing lets the next
     load fall through to whatever is actually true.

     A MISSING OR UNPARSEABLE TIMESTAMP COUNTS AS EXPIRED. Fail-closed:
     the only way to reach that state is a torn write or a hand-edited
     storage entry, and treating it as fresh would be trusting the one
     value that is demonstrably unreliable.

     Consent is read through a strict === '1', so anything unexpected
     reads as false. That direction is not arbitrary — a consent bug is
     only allowed to under-collect. */
  function readPending() {
    var token = (readKey(KEY_TOKEN) || '').trim();
    if (!token) return null;

    var ts = parseInt(readKey(KEY_TS), 10);
    if (!isFinite(ts) || (Date.now() - ts) > CLAIM_TTL_MS) {
      clearPending();
      return null;
    }

    return {
      token: token,
      email: (readKey(KEY_EMAIL) || '').trim(),
      consent: readKey(KEY_CONSENT) === '1'
    };
  }

  /* ⚠️ application/json IS LOAD-BEARING. DO NOT REVERT IT TO
     text/plain.

     ⚠️ AND NOTE HOW THIS ONE WAS FOUND, because Scenario 2's run
     history argues the opposite and the run history is misleading.
     This file shipped with text/plain and Scenario 2 has never
     visibly failed — but every clean run in its history was a curl
     test, sent with application/json. THE BROWSER PATH HAD NEVER
     ONCE PARSED. The bug was latent here and was surfaced by its
     twin, assets/rs-share.js, on Scenario 1. Nothing in this file's
     own history would have revealed it.

     THE TRADE, MEASURED IN BOTH DIRECTIONS:

     text/plain is one of the three CORS-safelisted request
     content-types, so the browser sends the POST directly with no
     preflight. That much was true and is still true. What it missed
     is what happens at the far end: MAKE DOES NOT JSON-PARSE A
     text/plain BODY. The request arrives as a single field named
     `value` holding the raw JSON string, so every downstream
     reference — here, claim_token and marketing_consent — resolves
     EMPTY. Filters then compare empty against empty and searches
     match the empty string, so the scenario does not error; it runs
     and does nothing.

     That is the worse failure by a distance. A preflight failure is
     loud: nothing reaches Make and the browser console says CORS. An
     unparsed body is silent, and on this surface it is silent in the
     way that matters most — the scenario reports success for a claim
     it never read, and nothing is credited.

     ⚠️ THE VERDICT READ DOES NOT RESCUE THIS. classifyBody() below
     now reads Make's {"ok":…} answer rather than trusting a 200, but
     it can only report the answer Make gives. A scenario that ran
     against an empty claim_token and concluded fine will say so, and
     this page will faithfully show the confirmation. The verdict
     catches a REFUSAL; it cannot catch a scenario that was never
     handed the token in the first place. application/json remains
     the only thing standing between the two.

     application/json is NOT safelisted, so it forces a preflight
     OPTIONS request first. Make's webhooks answer OPTIONS. If a
     future failure shows the claim dying BEFORE any POST lands in
     Make, the preflight is the first suspect and the browser console
     will name it — but the fix is on the endpoint, not here. THE
     PARSED BODY IS NON-NEGOTIABLE.

     UNCHANGED BY THE FLIP: the response still has to carry
     Access-Control-Allow-Origin for this script to read res.ok.
     Without a readable response there is no way to tell success from
     failure, and this page must not report success it cannot verify.
     mode 'no-cors' would fire the request but return an opaque
     response, which is exactly the guess this file refuses to make.

     assets/rs-share.js carries the identical constant and the
     identical reasoning. The two must not drift. */
  var POST_CONTENT_TYPE = 'application/json';

  /* Builds the panel swapper for a root. Shared by both arms — the two
     arms render DIFFERENT panels but the mechanism is identical, and
     duplicating it would let them drift.

     Absent panels are tolerated: a merchant can blank every setting in
     a state, in which case Liquid renders it empty and Dawn's div:empty
     collapses it. show() no-ops rather than throwing. */
  function makeShow(root) {
    var panels = root.querySelectorAll('[data-rs-state]');

    return function show(name) {
      var matched = null;
      for (var i = 0; i < panels.length; i++) {
        var panel = panels[i];
        var isTarget = panel.getAttribute('data-rs-state') === name;
        panel.classList.toggle('rs-rest__state--hidden', !isTarget);
        if (isTarget) matched = panel;
      }

      /* The visible content changed without a navigation, so move
         focus to the new panel — otherwise a screen-reader user gets
         no announcement that anything happened. tabindex is applied
         here rather than in the markup so the panels are not in the
         tab order the rest of the time. */
      if (matched) {
        matched.setAttribute('tabindex', '-1');
        try {
          matched.focus();
        } catch (e) {
          /* focus() on a detached or unfocusable node — non-fatal */
        }
      }
    };
  }

  /* Writes an address into an account-box node and reveals it together.

     THE TWO OPERATIONS ARE PAIRED ON PURPOSE: setting the text and
     removing the hidden class in one call means a failure in either
     direction leaves the line HIDDEN rather than blank-but-occupying-
     space. textContent, never innerHTML — the value came off a query
     string. */
  function fillEmailNode(root, selector, email) {
    if (!email) return;
    var node = root.querySelector(selector);
    if (!node) return;
    node.textContent = email;
    node.classList.remove(EMAIL_HIDDEN_CLASS);
  }

  /* Light validation, guarding RENDERING rather than security. The
     value is only ever written with textContent, so there is no
     injection surface, and a strict pattern would reject real
     addresses (unicode locals, long TLDs) for no gain. An '@' keeps
     arbitrary junk out of a line that claims to be an email; the
     length cap stops a multi-kilobyte value blowing the box open. */
  function looksLikeEmail(value) {
    return !!value && value.indexOf('@') !== -1 && value.length <= EMAIL_MAX_LENGTH;
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    /* ⚠️ THIS WAS THE SIGNED-IN BAIL. It is now a two-way router, and
       that is the whole shape of the inversion.

       The bail it replaced was load-bearing for one specific reason:
       the code past it rewrites the address bar, and a signed-in
       visitor holding an unclaimed ?t= would have their only copy of
       an unused gift stripped out of it. That protection has NOT been
       dropped — it moved into initSignedIn(), onto the no-pending-claim
       path, which does not scrub. Read the note there before touching
       either function; it is the guard most likely to be lost, because
       it is now an absence rather than a return. */
    if (root.hasAttribute(SIGNED_IN_ATTR)) {
      initSignedIn(root);
    } else {
      initSignedOut(root);
    }
  }

  /* ── PRE-AUTH ARM ──────────────────────────────────────────────────

     Reads the link, scrubs it, and binds a CTA that stores and
     navigates. NOTHING ON THIS ARM POSTS ANYTHING. */
  function initSignedOut(root) {
    var show = makeShow(root);

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      show('error'); // no URLSearchParams support: the token is unreadable
      return;
    }

    var token = (params.get(TOKEN_PARAM) || '').trim();
    var restored = false;

    /* ⚠️ RESTORE FROM STORAGE WHEN THE URL CARRIES NO TOKEN.

       WITHOUT THIS THE SIGN-OUT LOOP IS A DEAD END. The mismatch panel
       deliberately preserves the rs-claim-* keys so that signing out
       and back in with the right address resumes the claim — but the
       logout return lands here with a bare path, because return_to
       carries no query string. This arm read ?t= and nothing else, so a
       visitor with a perfectly good token sitting in storage was shown
       the ERROR panel: "we couldn't open this claim", about a claim
       that was right there. The preservation was real and nothing could
       reach it.

       THE URL WINS WHEN BOTH EXIST, and that ordering is deliberate. A
       fresh ?t= is an explicit act — the visitor just opened a link —
       whereas storage is a leftover from earlier in the session. If
       they arrive with a second, different invite, the one they clicked
       is the one they meant. The stored copy is then overwritten by the
       click handler below, so the two cannot stay out of step.

       `restored` is tracked because the two paths differ downstream:
       there is no query string to scrub on a restore, and the address
       comes from storage rather than from &e=. */
    var restoredEmail = '';
    if (!token) {
      var pending = readPending();
      if (pending) {
        token = pending.token;
        restoredEmail = pending.email;
        restored = true;
      }
    }

    /* THE ADDRESS FROM &e=.

       URLSearchParams.get() has already percent-decoded this, so the
       sender must encode it exactly once — a plus-addressed test
       recipient (sam+t3@…) arrives as a space if it is sent raw, and as
       a literal %2B if it is encoded twice.

       IT NOW HAS A SECOND, MORE IMPORTANT JOB than the account box it
       was written for: it is stored and becomes the expected address
       for the post-auth mismatch guard. The box it still fills belongs
       to the retired done panel, which nothing shows any more — that
       fill is left wired deliberately, so a revival of that panel does
       not silently render a blank line. */
    /* ON A RESTORE THERE IS NO &e= TO READ — the logout return carries
       a bare path — so the address comes back from storage alongside
       the token. It has to: it is the expected address for the
       post-auth mismatch guard, and losing it here would silently
       disarm that guard on exactly the journey the guard exists for.
       Someone who signed out BECAUSE of a mismatch would find it no
       longer checked on the way back in. */
    var email = (params.get(EMAIL_PARAM) || '').trim();
    if (!email && restored) email = restoredEmail;

    if (looksLikeEmail(email)) {
      fillEmailNode(root, EMAIL_NODE_SELECTOR, email);
    }

    /* Strip ONLY our own params; preserve everything else.

       SCRUBBED BEFORE THE TOKEN CHECK, not after, so a broken link does
       not leave the recipient's address sitting in the bar and in
       history while the error panel is up. `e` is scrubbed on every
       path this line is reached by.

       SAFE TO SCRUB ON THIS ARM even though the token is about to be
       needed: it has already been read into `token` above, and the
       click handler stores it from that variable rather than re-reading
       the URL. The signed-in arm is where scrubbing is destructive —
       see the guard there.

       ⚠️ DO NOT replace this with the pathname-only replaceState in
       assets/rs-form-validate.js:108. That form discards every query
       parameter, including utm_* campaign tags. Same constraint, and
       the same warning, as assets/rs-swatch-deeplink.js. Delete the two
       keys this file owns and rebuild; never replace wholesale. */
    params.delete(TOKEN_PARAM);
    params.delete(EMAIL_PARAM);
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    try {
      history.replaceState(null, '', url);
    } catch (e) {
      /* replaceState unavailable — non-fatal; the token and address stay
         visible in the address bar but the claim still works. */
    }

    /* No token, no claim. Checked AFTER the scrub above rather than
       before it so `e` is stripped on this path too — the error panel
       is exactly the case where the recipient's address should not be
       left sitting in the address bar. */
    if (!token) {
      show('error');
      return;
    }

    var loginUrl = (root.getAttribute('data-rs-login-url') || '').trim();
    var button = root.querySelector('[data-rs-rest-claim]');
    var consent = root.querySelector('[data-rs-rest-consent]');

    if (!button) return; // merchant blanked the button label — nothing to bind

    /* ⚠️ THE ENDPOINT IS NOT CHECKED ON THIS ARM ANY MORE, and its
       absence here is deliberate rather than an oversight. Nothing on
       this arm posts, so a bad endpoint cannot fail here — it fails
       after auth, where the check now lives. Showing the error state
       pre-auth for an endpoint this arm never touches would block a
       visitor from starting a claim that might well succeed.

       What IS checked is the login URL, because that is what this arm
       actually depends on. A blank one means the CTA has nowhere to
       send them, which is a dead button, so it fails up front exactly
       as the endpoint check used to. */
    if (!loginUrl) {
      show('error');
      return;
    }

    var leaving = false;

    button.addEventListener('click', function () {
      if (leaving) return; // double-tap guard, ahead of the disable
      leaving = true;
      button.disabled = true; // paints via the canonical's own :disabled rule

      /* ⚠️ ORDER IS LOAD-BEARING, AND A REFACTOR THAT BREAKS IT FAILS
         SILENTLY. Read .checked, write all three keys, THEN assign
         location — all in ONE SYNCHRONOUS TURN.

         Put an await, a fetch, or any promise between the write and
         the navigation and the browser may tear the page down
         mid-turn: the keys never land, the visitor authenticates, and
         they arrive back at a page with no pending claim and nothing
         to show for it. No error fires anywhere. The consent tick in
         particular is read HERE, at click time, on the only page where
         the checkbox exists — the panel carrying it is not rendered on
         the post-auth arm at all.

         Consent is stored as the STRING form of a real boolean and
         read back through a strict === '1' test. Anything else reads
         as false, so a torn or corrupted write under-collects consent
         rather than over-collecting it. */
      var consented = !!(consent && consent.checked);

      /* ⚠️ ONLY A PLAUSIBLE ADDRESS IS STORED; anything else is stored
         as empty, and that is a correctness guard rather than tidiness.

         The stored address becomes the EXPECTED address for the
         post-auth mismatch guard. Store an unusable &e= — junk with no
         '@', or a multi-kilobyte value — and that guard compares real
         against garbage, finds them different, and blocks a claim that
         was perfectly valid. Storing empty instead makes the guard skip
         itself, which is the documented behaviour when no expected
         address is known, and the claim proceeds.

         Same looksLikeEmail() test that gates the account-box fill
         above, deliberately: one definition of "usable address" for
         both jobs, so they cannot disagree about the same value. */
      writeKey(KEY_TOKEN, token);
      writeKey(KEY_EMAIL, looksLikeEmail(email) ? email : '');
      writeKey(KEY_CONSENT, consented ? '1' : '0');

      /* ⚠️ THE TIMESTAMP MUST BE WRITTEN LAST, AND IT IS THE ONE KEY
         readPending() TREATS AS THE GATE. A torn write that lands the
         token but not the stamp reads as expired and is discarded,
         which is the safe direction; the reverse order would let a
         tokenless stamp look like a fresh claim.

         Stamped even on a RE-store — a visitor who came back through
         the sign-out loop and clicks again gets a fresh six hours,
         because the act of clicking is what the window measures from,
         not the first time they ever saw the link. */
      writeKey(KEY_TS, String(Date.now()));

      /* ⚠️ NAVIGATING EVEN IF THE WRITES FAILED. writeKey returns false
         in privacy modes where sessionStorage throws, and this code
         deliberately ignores that.

         The alternative — block and show an error — is worse. The
         visitor still needs an account, the account is still worth
         having, and the gift is still recoverable by re-clicking the
         email link once they have one. Refusing to let them proceed
         would strand a claim that a later click can still complete.

         ⚠️ THIS IS A NEW FAILURE MODE INTRODUCED BY THE INVERSION, and
         it is worth knowing rather than discovering. The old flow
         posted before any navigation, so storage was never on the
         critical path. Now a visitor with sessionStorage unavailable
         completes auth and lands on the ordinary signed-in panel with
         no claim fired and no error shown. Accepted, not overlooked. */
      window.location.assign(loginUrl);
    });
  }

  /* ── POST-AUTH ARM ─────────────────────────────────────────────────

     Looks for a claim this page itself stored before sending the
     visitor to auth, and fires it. */
  function initSignedIn(root) {
    var show = makeShow(root);

    /* ROUTED THROUGH THE SHARED READER, same as the signed-out restore
       path, so the expiry rule and the consent coercion have exactly
       one definition. A tab left open past the window lands here with
       an expired token and is treated as having no pending claim at
       all — which is right: the ordinary signed-in panel is true for
       that visitor, and their route back is the email link. */
    var pending = readPending();
    var token = pending ? pending.token : '';

    /* ⚠️ NO PENDING CLAIM — AND CRITICALLY, NO URL SCRUB ON THIS PATH.

       THIS IS WHERE THE OLD SIGNED-IN BAIL WENT. That bail existed to
       keep the scrub away from a signed-in visitor arriving with an
       unclaimed ?t=: their address bar is the ONLY copy of a gift they
       have not used, and stripping it destroys their route back to it
       after signing out. The protection is unchanged; it is now
       expressed by this function simply never touching the URL.

       ⚠️ THAT MAKES IT AN ABSENCE RATHER THAN A GUARD, which is a
       weaker thing to notice. Do not "tidy" the scrub up into a shared
       helper called from both arms. It belongs to the pre-auth arm
       alone. */
    if (!token) return;

    /* THE MISMATCH GUARD.

       Compares the address the gift was sent to against the address
       actually signed in. Both are normalised the same way — the
       server does `| downcase | strip`, normalizeEmail() does the
       identical pair — because casing is the realistic divergence: the
       link carried sam@example.com and the account was created as
       Sam@Example.com.

       ⚠️ SKIPPED ENTIRELY WHEN THE STORED ADDRESS IS BLANK, and that
       is a decision. &e= is sender-controlled and may simply be absent
       from a link. With no expected address there is nothing to
       compare, and failing closed there would deny a valid claim for a
       link that merely omitted a parameter. So a claim with no stored
       address proceeds. The guard catches "signed in as the wrong
       person", which it can only do when it knows who the right person
       is. */
    var storedEmail = normalizeEmail(pending.email);
    var currentEmail = normalizeEmail(root.getAttribute('data-rs-customer-email'));

    if (storedEmail && currentEmail && storedEmail !== currentEmail) {
      fillEmailNode(root, MISMATCH_EMAIL_SELECTOR, storedEmail);

      /* The sign-out link is rendered but ships hidden, because the
         route behind it is unverified under New Customer Accounts and
         is emitted onto the root only when Liquid finds it non-blank.
         Revealing it here — rather than in the markup — means a blank
         route degrades to copy-only instead of to a dead anchor. */
      var logoutUrl = (root.getAttribute('data-rs-logout-url') || '').trim();
      var signout = root.querySelector('[data-rs-signout]');
      if (signout && logoutUrl) {
        signout.setAttribute('href', logoutUrl);
        signout.removeAttribute('hidden');
      }

      /* ⚠️ STORAGE IS NOT CLEARED HERE. This is a dead end for THIS
         session, not for the claim: signing out and back in with the
         right address resumes it exactly where it was. Clearing would
         turn a recoverable wrong turn into a lost gift. */
      show('mismatch');
      return;
    }

    var endpoint = (root.getAttribute('data-rs-endpoint') || '').trim();

    /* Checked here rather than pre-auth because this is the arm that
       posts. An unconfigured or non-https endpoint cannot be claimed
       against; the https test also stops a mixed-content request being
       attempted from a secure page. */
    if (endpoint.slice(0, 8) !== 'https://' || typeof window.fetch !== 'function') {
      show('error');
      return;
    }

    /* ⚠️ FIRE-ONCE: STORAGE IS CLEARED BEFORE THE REQUEST GOES OUT,
       NOT AFTER IT RESOLVES.

       A reload while the POST is in flight must not re-fire the claim.
       Scenario 2's first-claim-only gate makes a genuine double-fire
       harmless server-side, but the second response would race the
       first's UI — one resolving to confirm while the other resolves
       to error, on the same page, with no way to say which is true.
       Clearing first makes the second load find no pending claim and
       sit quietly on the `in` panel.

       THE COST, ACCEPTED: a claim that FAILS has already been cleared,
       so there is nothing left in this session to retry from. The
       recovery path is re-clicking the original email link, which
       still carries ?t= — the same recovery the mismatch panel points
       at, and the reason the error copy tells them to use it. Holding
       the token back to enable an in-page retry would reintroduce the
       double-fire this guard exists to prevent.

       The values are captured into locals first, so clearing cannot
       empty the payload out from under the request. */
    var consented = pending.consent;
    clearPending();

    show('claiming');

    postClaim(endpoint, token, consented)
      .then(function (verdict) {
        /* postClaim has already resolved the outcome to a panel name.
           Nothing is decided here — adding a test to this callback
           would put the HTTP-to-state mapping in two places. */
        show(verdict);
      })
      .catch(function (error) {
        /* NEVER fall through to 'confirm'. An unreachable webhook
           means we did not see the claim land, and telling the visitor
           it did would be worse than telling them nothing.

           ⚠️ A REFUSAL NO LONGER ARRIVES HERE, and this comment used
           to say it did. A non-2xx status and an explicit
           {"ok":false} both RESOLVE — with 'error' — and reach show()
           through the .then() above; see classifyBody(). What is left
           for this callback is requests that never completed, plus
           the abort below. The invariant is unchanged and now holds
           in two places; only the traffic through this one narrowed.

           That asymmetry matters more here than it did on the old
           signed-out flow: the pending claim has already been cleared
           by the time this can run, so a false success would strand a
           real gift with no in-session route back to it.

           ⚠️ A TIMEOUT IS NOT A FAILURE, AND IT USED TO BE TREATED AS
           ONE. The abort at REQUEST_TIMEOUT_MS fires on a request that
           has already left the browser — Make may have received and
           processed it perfectly well, and we simply stopped waiting.
           Calling that `error` tells someone whose gift landed that it
           did not, and sends them back to a link that is now spent.
           `processing` is what we actually know.

           Every other rejection is a request that demonstrably did not
           complete — DNS failure, connection refused, a CORS response
           the browser would not let us read — and stays `error`. */
        show(error && error.name === 'AbortError' ? 'processing' : 'error');
      });
  }

  /* ⚠️ CLASSIFIES A 2xx BODY, AND THE UNREADABLE CASE IS NOT AN ERROR.

     Scenario 2 answers a claim with {"ok":true|false,"reason":…} at
     HTTP 200 — the verdict is in the BODY, and the status says only
     that Make received the request. A 200 therefore proves nothing
     about the claim on its own, which is the whole reason this
     function exists.

     THREE OUTCOMES, AND THE THIRD IS THE INTERESTING ONE:

       ok === true    the claim landed. `confirm`.
       ok === false   Make refused it — already claimed, unknown token,
                      expired. `error`.
       anything else  `processing`.

     THAT LAST ROW IS DELIBERATELY WIDE. It catches a non-JSON body
     (Make's bare "Accepted" from a webhook whose response was never
     configured), valid JSON with no `ok`, a null, a bare scalar — any
     shape this file cannot read a verdict out of. In every one of
     those the request DID reach Make and may well have succeeded; we
     simply cannot see the answer.

     Neither of the other two panels is honest about that. `confirm`
     asserts a credit we did not observe, which is the exact lie this
     verdict work exists to remove. `error` asserts a failure we did
     not observe either, and would send someone whose gift landed off
     to re-open a link that is now spent. `processing` is the only
     state that describes what is actually known, and it points at
     the confirmation email — which comes from Make, and is therefore
     true whichever way the claim resolved.

     `ok` is tested with a strict typeof rather than for truthiness,
     so a string "false" cannot read as success. Same direction as the
     consent coercion above: the ambiguous shape falls to the
     non-committal answer, never to the reassuring one. */
  function classifyBody(text) {
    var verdict;

    try {
      verdict = JSON.parse(text);
    } catch (e) {
      return 'processing'; // not JSON at all
    }

    if (!verdict || typeof verdict.ok !== 'boolean') return 'processing';

    return verdict.ok ? 'confirm' : 'error';
  }

  /* RESOLVES WITH A PANEL NAME, never with the Response. The caller
     shows what it is handed and does no classification of its own, so
     there is exactly one place where an HTTP result becomes a state.

     It rejects only on a genuine fetch failure — network down, CORS
     refused, or the abort below. */
  function postClaim(endpoint, token, marketingConsent) {
    /* Abort on a stall. Without this a hung connection leaves the
       button disabled and the page in the claim state indefinitely,
       with no signal to the visitor — fetch has no native timeout. */
    var controller = ('AbortController' in window) ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, REQUEST_TIMEOUT_MS)
      : null;

    var options = {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit', // nothing here is authenticated by cookie
      cache: 'no-store',
      headers: { 'Content-Type': POST_CONTENT_TYPE },
      body: JSON.stringify({
        claim_token: token,
        marketing_consent: marketingConsent // a real boolean, always sent
      })
    };
    if (controller) options.signal = controller.signal;

    return fetch(endpoint, options).then(
      function (response) {
        /* A 4xx/5xx is a resolved fetch, not a rejection — an
           unchecked .then() here would report a rejected claim as a
           successful one. The body is not read on this path: a Make
           error response carries no verdict worth classifying, and
           reading it would only delay the panel. */
        if (!response.ok) {
          if (timer) clearTimeout(timer);
          return 'error';
        }

        /* ⚠️ THE BODY IS READ BEFORE THE TIMER IS CLEARED, AND THE
           ORDER IS THE POINT.

           fetch resolves as soon as the HEADERS arrive; the body is
           still streaming at that moment. Clearing the timeout here —
           where it used to be cleared, back when nothing read the body
           — would take the abort signal off the request just as the
           only remaining unbounded operation begins. A connection that
           delivers headers and then stalls mid-body would hang with no
           timeout left to catch it, and the page would sit on
           `claiming` indefinitely: precisely the failure
           REQUEST_TIMEOUT_MS exists to prevent, reintroduced one step
           further along.

           So the timer is cleared inside the text() settle, on both
           its paths. An abort landing during the read rejects this
           promise with an AbortError, which the caller maps to
           `processing` exactly as it maps a pre-headers abort. */
        return response.text().then(
          function (text) {
            if (timer) clearTimeout(timer);
            return classifyBody(text);
          },
          function (error) {
            if (timer) clearTimeout(timer);
            throw error;
          }
        );
      },
      function (error) {
        if (timer) clearTimeout(timer);
        throw error;
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
